import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTripPlan } from "@/hooks/useTripPlan";
import { getTripPool } from "@/lib/publicGuide";
import { createBooking } from "@/lib/bookings";
import { supabase } from "@/lib/supabase";

type Status = "idle" | "loading" | "success" | "error";

export type EnquiryKind = "experience" | "farm" | "farmer" | "trip";

export interface EnquiryOption {
  value: string;
  label: string;
}

/** `notify` reuses the farm enquiry but asks for a reopening alert instead of dates. */
export type EnquiryIntent = "enquire" | "notify";

interface EnquiryFormProps {
  kind: EnquiryKind;
  options: EnquiryOption[];
  defaultValue: string;
  intent?: EnquiryIntent;
}

const SUBMIT_URL = "https://readdy.ai/api/form/dalsgo1np3c7gfp1oqbg";

const KIND_META: Record<
  EnquiryKind,
  { fieldLabel: string; selectName: string; heading: string; placeholder: string }
> = {
  experience: {
    fieldLabel: "Experience",
    selectName: "experience",
    heading: "Enquire About This Experience",
    placeholder: "Tell us about your group, interests or any special requests.",
  },
  farm: {
    fieldLabel: "Farm",
    selectName: "farm",
    heading: "Enquire About This Farm",
    placeholder: "Tell us about your group, the season you are planning or any special requests.",
  },
  farmer: {
    fieldLabel: "Farmer",
    selectName: "farmer",
    heading: "Enquire About This Farmer",
    placeholder: "Tell us about your group, the season you are planning or any special requests.",
  },
  trip: {
    fieldLabel: "Itinerary",
    selectName: "itinerary",
    heading: "Build My Itinerary",
    placeholder: "Tell us your travel dates, group size and the experiences you'd love to include.",
  },
};

/** Turns the saved itinerary store into a readable block of text. */
function buildTripPlanText(plan: Record<number, string[]>, dayCount: number): string {
  const lines: string[] = [];
  for (let day = 0; day < dayCount; day += 1) {
    const titles = (plan[day] || [])
      .map((id) => getTripPool().find((item) => item.id === id)?.title)
      .filter((title): title is string => Boolean(title));
    if (titles.length > 0) {
      lines.push(`Day ${day + 1}: ${titles.join(", ")}`);
    }
  }
  return lines.join("\n");
}

export default function EnquiryForm({
  kind,
  options,
  defaultValue,
  intent = "enquire",
}: EnquiryFormProps) {
  const meta = KIND_META[kind];
  const isNotify = intent === "notify";
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const { plan, dayCount, totalStops } = useTripPlan();
  const tripPlanText = useMemo(() => buildTripPlanText(plan, dayCount), [plan, dayCount]);
  const hasTripPlan = totalStops > 0 && tripPlanText.length > 0;
  const defaultMessage = hasTripPlan && tripPlanText.length <= 470 ? `${tripPlanText}\n\n` : "";

  // The reopening notice needs no dates or group size - it just needs to reach
  // the right farm. A prefilled message keeps the intent obvious to the team.
  const notifyPreset = `Please let me know as soon as ${
    defaultValue || "this farm"
  } reopens, and which dates and activities will be available.`;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("Thank you! Your request has been received. We will reply shortly.");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const subject = String(formData.get(meta.selectName) || "").trim();
    const preferredDate = String(formData.get("preferred_date") || "").trim();
    const groupSizeRaw = String(formData.get("group_size") || "").trim();
    const message = String(formData.get("message") || "").trim();
    const itinerary = String(formData.get("trip_plan") || "").trim();

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    setStatus("loading");
    setMessage("");

    const sourcePage = typeof window !== "undefined" ? window.location.pathname : null;

    // Save the booking to the backend dashboard AND notify the team by email.
    // Both run together; the request succeeds if either one lands.
    const bookingSave = createBooking({
      booking_type: kind,
      subject,
      name,
      email,
      phone,
      preferred_date: preferredDate || null,
      group_size: groupSizeRaw ? Number(groupSizeRaw) : null,
      message,
      itinerary,
      source_page: sourcePage,
    })
      .then(() => true)
      .catch(() => false);

    const emailSend = fetch(SUBMIT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: payload.toString(),
    })
      .then(async (response) => {
        const responseText = await response.text();
        let parsed: {
          code?: string;
          message?: string;
          meta?: { message?: string; detail?: string };
        } | null = null;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          parsed = null;
        }
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
        return {
          ok: response.ok && parsed?.code === "OK" && !isSpam,
          message: typeof serverMsg === "string" ? serverMsg : "",
        };
      })
      .catch(() => ({
        ok: false,
        message: "Network error. Please check your connection and try again.",
      }));

    const [bookingSaved, emailResult] = await Promise.all([bookingSave, emailSend]);

    if (bookingSaved || emailResult.ok) {
      if (bookingSaved) {
        // Send the guest their confirmation email. Fire-and-forget so a mail
        // hiccup can never block the booking; it no-ops until Resend is set up.
        supabase.functions
          .invoke("send-booking-confirmation", {
            body: {
              name,
              email,
              booking_type: kind,
              subject,
              preferred_date: preferredDate || null,
              group_size: groupSizeRaw || null,
              itinerary,
            },
          })
          .catch(() => {});
      }
      setStatus("success");
      setMessage(
        "Thank you! Your booking request has been received. We will confirm availability within 24 hours.",
      );
      form.reset();
    } else {
      setStatus("error");
      setMessage(emailResult.message || "Something went wrong. Please try again.");
    }
  };

  const inputClass =
    "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

  return (
    <div className="bg-primary-900 rounded-lg p-7 md:p-9">
      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
        <i className="ri-mail-send-line text-2xl"></i>
      </span>
      <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mt-5">
        {isNotify ? "Notify Me When It Reopens" : meta.heading}
      </h3>
      <p className="text-sm text-background-200 leading-relaxed mt-3">
        {isNotify
          ? "Leave your name and email and we will message you the moment this farm starts taking visits again - no dates needed yet."
          : "Share a few details and our team will get back with availability, a clear quote and a suggested plan."}
      </p>

      <form
        id="rythugaru-enquiry-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-7 space-y-4"
      >
        <input type="hidden" name="trip_plan" value={tripPlanText} />

        {hasTripPlan ? (
          <div className="flex items-start gap-3 rounded-md border border-primary-700 bg-primary-800 px-4 py-3">
            <i className="ri-route-line text-lg text-accent-300"></i>
            <div>
              <p className="text-sm font-semibold text-background-50">
                Your saved trip plan is attached
              </p>
              <p className="text-xs text-background-200 mt-0.5">
                Your {dayCount}-day itinerary ({totalStops} stops) travels with this enquiry so we
                can plan around it.
              </p>
            </div>
          </div>
        ) : kind === "trip" ? (
          <div className="flex items-start gap-3 rounded-md border border-primary-700 bg-primary-800 px-4 py-3">
            <i className="ri-magic-line text-lg text-accent-300"></i>
            <div>
              <p className="text-sm font-semibold text-background-50">We'll build it for you</p>
              <p className="text-xs text-background-200 mt-0.5">
                Share your dates and what you'd love to see — our team will craft a day-by-day
                Konaseema itinerary and reply within 24 hours.
              </p>
            </div>
          </div>
        ) : isNotify ? null : (
          <div className="flex items-start gap-3 rounded-md border border-primary-700 bg-primary-800 px-4 py-3">
            <i className="ri-map-2-line text-lg text-accent-300"></i>
            <div>
              <p className="text-sm font-semibold text-background-50">
                Planning more than one stop?
              </p>
              <p className="text-xs text-background-200 mt-0.5">
                Build a day-by-day itinerary and it will travel with this enquiry automatically.
              </p>
              <Link
                to="/plan-your-trip"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-accent-300 hover:text-accent-200 transition-colors cursor-pointer"
              >
                Plan your trip
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="eq-name" className={labelClass}>
            Your name
          </label>
          <input
            id="eq-name"
            name="name"
            type="text"
            required
            placeholder="e.g. Ananya Sharma"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-email" className={labelClass}>
              Email
            </label>
            <input
              id="eq-email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="eq-phone" className={labelClass}>
              Phone
            </label>
            <input
              id="eq-phone"
              name="phone"
              type="tel"
              placeholder="+91 ..."
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="eq-subject" className={labelClass}>
            {meta.fieldLabel}
          </label>
          <select
            id="eq-subject"
            name={meta.selectName}
            defaultValue={defaultValue}
            className={`${inputClass} cursor-pointer`}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {!isNotify && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="eq-date" className={labelClass}>
              Preferred date
            </label>
            <input
              id="eq-date"
              name="preferred_date"
              type="date"
              className={`${inputClass} cursor-pointer`}
            />
          </div>
          <div>
            <label htmlFor="eq-group" className={labelClass}>
              Group size
            </label>
            <input
              id="eq-group"
              name="group_size"
              type="number"
              min="1"
              placeholder="e.g. 4"
              className={inputClass}
            />
          </div>
        </div>
        )}

        <div>
          <label htmlFor="eq-message" className={labelClass}>
            Your message
          </label>
          <textarea
            id="eq-message"
            name="message"
            rows={4}
            maxLength={500}
            defaultValue={isNotify ? notifyPreset : defaultMessage}
            placeholder={
              isNotify
                ? "Anything you'd like to know before you visit? (optional)"
                : meta.placeholder
            }
            className={`${inputClass} resize-none`}
          ></textarea>
        </div>

        <div className="form-extra-contact" aria-hidden="true">
          <label htmlFor="eq-website-alt">Leave this field empty</label>
          <input
            id="eq-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {status === "loading"
            ? "Sending..."
            : isNotify
              ? "Notify Me When It Reopens"
              : "Send Enquiry"}
          {status !== "loading" && (
            <i className={isNotify ? "ri-notification-3-line" : "ri-send-plane-fill"}></i>
          )}
        </button>

        {status === "success" && (
          <p className="flex items-start gap-2 text-sm text-primary-200 bg-primary-800 border border-primary-700 rounded-md px-4 py-3">
            <i className="ri-checkbox-circle-fill text-lg"></i>
            <span>{message}</span>
          </p>
        )}
        {status === "error" && (
          <p className="flex items-start gap-2 text-sm text-accent-200 bg-primary-800 border border-accent-700 rounded-md px-4 py-3">
            <i className="ri-error-warning-fill text-lg"></i>
            <span>{message}</span>
          </p>
        )}
      </form>
    </div>
  );
}